import { useMemo, useContext, useState } from 'react';
import { createColumnHelper, Row, ColumnDef } from '@tanstack/react-table';
import { AdminProfessionals } from '@platform/client';
import {
  ProVettingResult,
  AdminProExportResponse,
  AdminProfessional,
} from '@platform/client/schemas';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';
import { UpdateCohortModal } from './UpdateCohortModal';
import { SingleProVettingModal, BatchProVettingModal } from './UpdateVettingModal';
import { keepPreviousData } from '@tanstack/react-query';
import { PillBadge, PillBadgeColors, Icon } from '@platform/components';
import FlexibleLink from '../../../util/FlexibleLink';
import { VerifyEmailModal } from './VerifyEmailModal';

export const ProfessionalsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Professionals'}>
      <ProfessionalsAdminContent />
    </AdminListPageWrapper>
  );
};

type UpdateCohortModalData = {
  userId: number;
  currentCohort: string;
  currentPartner: string;
  proName: string;
};

type UpdateVettingModalData = {
  userId: number;
  currentVettingResult: ProVettingResult;
  proName: string;
};

const columnHelper = createColumnHelper<AdminProfessional>();

const ProfessionalsAdminContent = () => {
  const { rowSelection, adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [updateCohortData, setUpdateCohortData] = useState<UpdateCohortModalData>();
  const [vettingModalData, setVettingModalData] = useState<UpdateVettingModalData>();
  const [batchVettingModalOpen, setBatchVettingModalOpen] = useState(false);
  const [emailVerifyUserId, setEmailVerifyUserId] = useState<number | undefined>(undefined);
  const { data, isSuccess, isLoading, isError } = AdminProfessionals.useGetAdminProfessionals(
    {
      ...adminQueryOptionsV2,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { professionals = [], filteredCount = 0 } = data || {};

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: 'selected',
          header: ({ table }) => (
            <div className='flex flex-col items-center gap-3'>
              <label>Select</label>
              <input
                type='checkbox'
                className='h-6 w-6'
                onChange={table.getToggleAllRowsSelectedHandler()}
                checked={table.getIsAllRowsSelected()}
              />
            </div>
          ),
          cell: ({ row }) => (
            <div className='flex items-center justify-center'>
              <input
                type='checkbox'
                className='h-4 w-4'
                onChange={row.getToggleSelectedHandler()}
                checked={row.getIsSelected()}
                aria-label='Select Row'
              />
            </div>
          ),
          size: 25,
        }),
        columnHelper.accessor('id', {
          header: 'Id',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: ({ row }) => {
            return (
              <FlexibleLink
                type='legacy'
                href={`/professionals/${row.original.id}`}
                className='text-brand-100 hover:underline'
              >
                {row.original.name}
              </FlexibleLink>
            );
          },
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('email', {
          header: 'Email',
          cell: ({ row }) => {
            return (
              <div className='flex gap-2 justify-between'>
                <span>{row.original.email}</span>
                {!row.original.emailVerified && (
                  <button
                    onClick={() => {
                      setEmailVerifyUserId(row.original.id);
                    }}
                  >
                    <Icon type='alert-circle' size='18' fill='red' stroke='white' />
                  </button>
                )}
              </div>
            );
          },
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('skills', {
          header: 'Skills',
          cell: (skills) => {
            return skills.getValue().map((skill: string) => (
              <PillBadge variant='plain' color='indigo' key={skill} size='sm'>
                {skill}
              </PillBadge>
            ));
          },
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('partnerName', {
          header: 'Partner',
          cell: (partnerName) => {
            return <span>{partnerName.getValue()}</span>;
          },
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('cohortName', {
          header: 'Cohort',
          cell: ({ row }) => {
            return (
              <div className='flex justify-between'>
                <span>{row.original.cohortName}</span>
                <button
                  onClick={() =>
                    setUpdateCohortData({
                      userId: row.original.id,
                      currentCohort: row.original.cohortName ?? '',
                      currentPartner: row.original.partnerName ?? '',
                      proName: row.original.name,
                    })
                  }
                >
                  <Icon type='edit-01' size='14' />
                </button>
              </div>
            );
          },
          meta: {
            filterVariant: 'text',
          },
        }),

        columnHelper.accessor('vettingResult', {
          header: 'Vetting Result',
          cell: ({ row }) => {
            return (
              <div className='flex justify-between'>
                <ProVettingBadge row={row} />
                <button
                  onClick={() =>
                    setVettingModalData({
                      userId: row.original.id,
                      currentVettingResult: row.original.vettingResult,
                      proName: row.original.name,
                    })
                  }
                >
                  <Icon type='edit-01' size='14' />
                </button>
              </div>
            );
          },
          meta: {
            filterVariant: 'text',
          },
          // for now this is disabled as the db around vetting is a mess of determined values
          enableSorting: false,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => {
            return <span>{new Date(createdAt.getValue()).toLocaleDateString()}</span>;
          },
        }),
      ] as ColumnDef<AdminProfessional>[],
    [],
  );

  return (
    <>
      <AdminTable
        columns={columns}
        data={professionals}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={filteredCount}
        enableFiltering={true}
        enableMultiRowSelection={true}
        getRowId={(row) => String(row.id)}
        tableActions={[
          {
            text: 'Batch vetting',
            callback: () => setBatchVettingModalOpen(true),
            isDisabled: () => Object.keys(rowSelection ?? {}).length === 0,
          },
        ]}
        exportOptions={{
          exportQuery: AdminProfessionals.useGetUserExportData,
          dataToExportFn: (data: AdminProExportResponse) => data.professionals,
          exportFileName: `pro_user_export_${new Date().toISOString().replace(' ', '_')}.csv`,
        }}
      />
      {updateCohortData && (
        <UpdateCohortModal
          userId={updateCohortData.userId}
          currentCohort={updateCohortData.currentCohort}
          currentPartner={updateCohortData.currentPartner}
          onClose={() => setUpdateCohortData(undefined)}
          proName={updateCohortData.proName}
        />
      )}
      {emailVerifyUserId && (
        <VerifyEmailModal
          userId={emailVerifyUserId}
          onClose={() => setEmailVerifyUserId(undefined)}
        />
      )}
      {vettingModalData && (
        <SingleProVettingModal
          onClose={() => setVettingModalData(undefined)}
          userId={vettingModalData.userId}
          currentVettingResult={vettingModalData.currentVettingResult}
          proName={vettingModalData.proName}
        />
      )}
      {batchVettingModalOpen && (
        <BatchProVettingModal onClose={() => setBatchVettingModalOpen(false)} />
      )}
    </>
  );
};

const ProVettingBadge = ({ row }: { row: Row<AdminProfessional> }) => {
  const { vettingResult } = row.original;
  let badgeColor: PillBadgeColors;
  switch (vettingResult) {
    case ProVettingResult.Accepted:
      badgeColor = 'success-500';
      break;
    case ProVettingResult.Rejected:
      badgeColor = 'orange';
      break;
    case ProVettingResult.Applied:
      badgeColor = 'warning';
      break;
    case ProVettingResult.Waitlist:
      badgeColor = 'warning';
      break;
    case ProVettingResult.Signup_Incomplete:
      badgeColor = 'gray';
      break;
    case ProVettingResult.Disabled:
      badgeColor = 'error';
      break;
    default:
      throw new Error(`Unknown vetting result: ${vettingResult}`);
  }
  return (
    <PillBadge variant='plain' color={badgeColor} size='sm'>
      {vettingResult.split('_').join(' ').toUpperCase()}
    </PillBadge>
  );
};
